import { useState } from 'react'
import { FiDownload, FiArrowRight, FiAlertTriangle, FiCheck } from 'react-icons/fi'
import {
  PageHeader, Stat, Sparkline, Card, Tabs, Pill, Avatar, Btn,
} from '../../ui'
import AppShellV2 from '../../layouts/AppShellV2'

const SPARK_JORNADA = [11, 10, 12, 11, 13, 12, 14, 13, 14]
const SPARK_PAUSA   = [2, 3, 2, 4, 3, 2, 3, 4, 2]
const SPARK_AUSEN   = [1, 1, 2, 1, 2, 2, 1, 2, 1]
const SPARK_HORAS   = [78, 80, 82, 79, 84, 86, 83, 85, 84]

const HOURS = [
  { h: '07', v: 2 },  { h: '08', v: 9 },  { h: '09', v: 16 }, { h: '10', v: 17 },
  { h: '11', v: 16 }, { h: '12', v: 12 }, { h: '13', v: 6 },  { h: '14', v: 4 },
  { h: '15', v: 12 }, { h: '16', v: 14 }, { h: '17', v: 13 }, { h: '18', v: 8 },
  { h: '19', v: 3 },
]

const TODAY = [
  { init: 'MG', name: 'María García',  hue: 195, role: 'Diseñadora',  ein: '08:58', pausa: '00:30', estado: 'jornada', centro: 'Madrid Norte' },
  { init: 'CL', name: 'Carlos López',  hue: 250, role: 'Comercial',   ein: '09:02', pausa: '13:30', estado: 'pausa',   centro: 'Madrid Norte' },
  { init: 'AM', name: 'Ana Martínez',  hue: 25,  role: 'Soporte',     ein: '09:05', pausa: '00:15', estado: 'jornada', centro: 'Toledo Sur' },
  { init: 'JR', name: 'Javier Ruiz',   hue: 130, role: 'Técnico',     ein: '—',     pausa: '—',     estado: 'ausente', centro: 'Madrid Norte' },
  { init: 'LM', name: 'Laura Morales', hue: 320, role: 'Operaciones', ein: '08:45', pausa: '00:25', estado: 'jornada', centro: 'Toledo Sur' },
]

const PENDING = [
  { tone: 'warn',   icon: FiAlertTriangle, title: 'Carlos L. — pausa olvidada', sub: '13:30 sin reanudar · 12 abr' },
  { tone: 'danger', icon: FiAlertTriangle, title: 'Javier R. — sin fichaje',     sub: 'Falta entrada de hoy · 13 abr' },
  { tone: 'ok',     icon: FiCheck,         title: 'Ana M. — corrección aceptada', sub: 'Pausa 12:30–13:00 · aprobada' },
]

function EstadoPill({ estado }) {
  if (estado === 'jornada') return <Pill tone="emerald"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> En jornada</Pill>
  if (estado === 'pausa') return <Pill tone="amber"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" /> En pausa</Pill>
  return <Pill tone="red"><span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" /> Ausente</Pill>
}

function PresenceChart() {
  const max = Math.max(...HOURS.map(h => h.v))
  return (
    <div>
      <div className="flex items-end gap-1 h-24">
        {HOURS.map((h, i) => {
          const pct = (h.v / max) * 100
          const peak = h.v === max
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div
                className={`w-full rounded-t-md ${peak ? 'ia-grad-bg' : 'bg-[var(--ink-3)]/30'}`}
                style={{ height: `${pct}%` }}
              />
            </div>
          )
        })}
      </div>
      <div className="flex gap-1 mt-1.5">
        {HOURS.map((h, i) => (
          <div key={i} className="flex-1 text-center text-[9px] font-mono text-[var(--ink-4)]">{h.h}</div>
        ))}
      </div>
    </div>
  )
}

function DashboardContent() {
  const [vista, setVista] = useState('lista')

  return (
    <>
      <PageHeader
        num="§ 01"
        kicker="Resumen"
        title="Buenos días, Rosa."
        italic="Esto es lo que pasa hoy."
        sub="13 de abril 2026 · Lunes · cumplimiento legal al día"
        actions={
          <>
            <Btn variant="ghost" size="sm"><FiDownload size={13} /> Exportar</Btn>
            <Btn variant="primary" size="sm">Nuevo fichaje <FiArrowRight size={13} /></Btn>
          </>
        }
      />

      <div className="px-7 py-6 space-y-6">
        {/* StatCards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat
            label="En jornada"
            value="14"
            unit="/ 18"
            delta="+1"
            deltaTone="emerald"
            sub="vs. ayer mismo hora"
            spark={<Sparkline data={SPARK_JORNADA} color="#10B981" />}
          />
          <Stat
            label="En pausa"
            value="2"
            delta="−1"
            deltaTone="emerald"
            sub="≤ 30 min comida"
            spark={<Sparkline data={SPARK_PAUSA} color="#F59E0B" />}
          />
          <Stat
            label="Ausentes"
            value="1"
            delta="0"
            deltaTone="neutral"
            sub="1 ausencia justificada"
            spark={<Sparkline data={SPARK_AUSEN} color="#EF4444" />}
          />
          <Stat
            label="Horas previstas"
            value="84,5"
            unit="h"
            delta="+2,3 h"
            deltaTone="emerald"
            sub="vs. semana anterior"
            spark={<Sparkline data={SPARK_HORAS} color="#7C3AED" />}
          />
        </div>

        {/* Grid principal */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Tabla principal */}
          <div className="lg:col-span-8">
            <Card
              eyebrow="Estado en directo"
              title="Quién está fichado ahora"
              action={
                <Tabs
                  value={vista}
                  onChange={setVista}
                  items={[{ value: 'lista', label: 'Lista' }, { value: 'timeline', label: 'Línea de tiempo' }]}
                />
              }
              bodyClassName="p-0"
            >
              <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] bg-[var(--bg-2)]/50 border-b border-[var(--line)]">
                <div className="col-span-5">Empleado</div>
                <div className="col-span-2">Entrada</div>
                <div className="col-span-2">Pausa</div>
                <div className="col-span-2">Centro</div>
                <div className="col-span-1 text-right">Estado</div>
              </div>
              <div className="divide-y divide-[var(--line)]">
                {TODAY.map((e, i) => (
                  <div key={i} className="grid grid-cols-12 items-center px-5 py-3 text-[12.5px] hover:bg-[var(--bg-2)]/40 transition-colors">
                    <div className="col-span-5 flex items-center gap-2.5 min-w-0">
                      <Avatar name={e.name} hue={e.hue} size={28} />
                      <div className="min-w-0">
                        <div className="font-medium leading-tight truncate">{e.name}</div>
                        <div className="text-[10.5px] text-[var(--ink-3)]">{e.role}</div>
                      </div>
                    </div>
                    <div className="col-span-2 font-mono tnum text-[var(--ink-2)]">{e.ein}</div>
                    <div className="col-span-2 font-mono tnum text-[var(--ink-3)]">{e.pausa}</div>
                    <div className="col-span-2 text-[var(--ink-3)] truncate">{e.centro}</div>
                    <div className="col-span-1 flex justify-end"><EstadoPill estado={e.estado} /></div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Sidebar derecha */}
          <div className="lg:col-span-4 space-y-4">
            <Card
              eyebrow="Pendientes"
              title="Acciones (3)"
              action={<button type="button" className="text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-1">Revisar <FiArrowRight size={11} /></button>}
            >
              <ul className="space-y-3">
                {PENDING.map((p, i) => {
                  const Icn = p.icon
                  const bg = p.tone === 'warn' ? 'bg-[rgba(245,158,11,.10)]'
                           : p.tone === 'danger' ? 'bg-[rgba(239,68,68,.10)]'
                           : 'bg-[rgba(16,185,129,.10)]'
                  const fg = p.tone === 'warn' ? 'text-[#A86A06]'
                           : p.tone === 'danger' ? 'text-[#C13030]'
                           : 'text-[#0F8C66]'
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`mt-0.5 w-7 h-7 rounded-soft grid place-items-center flex-shrink-0 ${bg}`}>
                        <Icn size={13} className={fg} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium text-[var(--ink)] leading-tight">{p.title}</div>
                        <div className="text-[11.5px] text-[var(--ink-3)] mt-0.5">{p.sub}</div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </Card>

            <Card eyebrow="Hoy" title="Presencia por hora">
              <PresenceChart />
              <div className="mt-3 flex items-center justify-between text-[11.5px] text-[var(--ink-3)]">
                <span>Pico a las <span className="font-mono text-[var(--ink-2)] tnum">10:00</span> con <span className="font-mono text-[var(--ink-2)] tnum">17</span> personas</span>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}

export default function DashboardV2() {
  return (
    <AppShellV2 activeId="dashboard">
      <DashboardContent />
    </AppShellV2>
  )
}
